/* eslint-disable react/prop-types */
import React, { FC, useContext, useMemo } from 'react';
import { T, t } from '@sm/intl';
import { LogoWithText, useSurveyTheme, StaticContext } from '@sm/webassets';
import useStyles from './useStyles';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface FooterPoweredByLogoBlockProps {
  /** A boolean prop for grayscale themes. */
  isGrayScale: boolean;
}
// TODO: add defineMessages when ready for translations
const DEFAULT_COPY = {
  POWERED_BY: {
    id: 'FooterPoweredByLogoBlock.PoweredBy',
    defaultMessage: 'Powered by',
    description: '[Type: Paragraph][Vis: high] - Powered by text',
  },
  SEE_HOW: {
    id: 'FooterPoweredByLogoBlock.SeeHow',
    defaultMessage: 'See how easy it is to',
    description: '[Type: Paragraph][Vis: high] - See how easy it is to text',
  },
  CREATE_SURVEY: {
    id: 'FooterPoweredByLogoBlock.CreateSurvey',
    defaultMessage: 'create a survey.',
    description: '[Type: Link][Vis: high] - create a survey',
  },
  LOGO: {
    id: 'FooterPoweredByLogoBlock.Logo',
    defaultMessage: 'Survey monkey logo',
    description: '[Type: Link][Vis: low] - Survey monkey logo alt text for link in image',
  },
};

const FooterPoweredByLogoBlock: FC<FooterPoweredByLogoBlockProps> = ({ isGrayScale }) => {
  const {
    environment: { domain, subdomain, tld },
  } = useContext(StaticContext);

  const CURRENT_DOMAIN_URL = `https://${subdomain}.${domain}.${tld}`;
  const POWERED_BY_SURVEY_MONKEY_LOGO_URL = `${CURRENT_DOMAIN_URL}/?ut_source=survey_poweredby_home`;
  const CREATE_SURVEY_LINK_HOW_IT_WORKS = `${CURRENT_DOMAIN_URL}/mp/take-a-tour/?ut_source=survey_poweredby_howitworks`;

  const surveyTheme = useSurveyTheme();
  const logoColor = useMemo(() => {
    const isDarkBackground = surveyTheme.isDark;
    if (isDarkBackground) {
      return '#FFFFFF';
    }
    return isGrayScale ? 'rgba(0,0,0, 0.5)' : '';
  }, [isGrayScale, surveyTheme.isDark]);

  const { container, text, link, logo, imageLink } = useStyles({ surveyTheme, logoColor });
  return (
    <div className={container}>
      <p className={text}>
        <T desc={DEFAULT_COPY.POWERED_BY} />
      </p>
      <a
        className={imageLink}
        rel="noreferrer"
        target="_blank"
        href={POWERED_BY_SURVEY_MONKEY_LOGO_URL}
        aria-label={t(DEFAULT_COPY.LOGO)}
        data-testid="FooterPoweredByLogoBlock__Logo"
      >
        <span role="presentation" aria-hidden="true">
          <LogoWithText className={logo} />
        </span>
      </a>
      <p className={text}>
        <T desc={DEFAULT_COPY.SEE_HOW} />{' '}
        <a className={link} target="_blank" href={CREATE_SURVEY_LINK_HOW_IT_WORKS} rel="noreferrer">
          <T desc={DEFAULT_COPY.CREATE_SURVEY} />
        </a>
      </p>
    </div>
  );
};

export default FooterPoweredByLogoBlock;
